/**
 * Автосбор посевного плана от целевой структуры площадей (AK-791, ADR-075).
 *
 * Третий способ заполнения плана. Два прежних идут ОТ ПОЛЯ («после пшеницы
 * сеем подсолнечник», «подбери по кольцу») и просто добавляют культуру в
 * пустое поле. Этот идёт ОТ ЦЕЛИ («озимой пшеницы 18 567 га») и делит
 * ОГРАНИЧЕННЫЙ ресурс — гектары цели. Отсюда всё остальное: порядок разбора
 * сверху вниз, остаток квоты и честный отчёт «что не разложилось и почему».
 *
 * Два шага — не формальность
 * --------------------------
 * `preview` не пишет в базу ничего, `apply` пересчитывает раскладку заново по
 * тому же телу и сверяет отпечаток входов. Раскладка трогает сотни строк:
 * если бы кнопка писала сразу, единственным «отменить» была бы ручная чистка
 * семисот полей — после первого неудачного прогона человек перестал бы её
 * нажимать.
 *
 * Язык наружу — агронома: «собрать план автоматически», «цель по культуре»,
 * «разложилось / не разложилось», «собрано автоматически». Слов auto-fill,
 * digest, scenario и кодов причин на экране нет — коды живут здесь и
 * превращаются в текст ровно в одном месте.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { type PlanRowOrigin, rotationMatchKeys } from '@/lib/planning/planCrops';
import { cropStructureKeys, planVariantKeys, techCardKeys } from '@/lib/planning/queryKeys';
import { techCardErrorText } from '@/lib/planning/techCards';
import { toast } from '@/lib/toast';

// ── Способы заполнения (пункты одной кнопки) ─────────────────────────────────

/**
 * Способы заполнения плана. Кнопка на экране ОДНА («Заполнить план»), способ
 * выбирается в её меню — решение founder 04.09 п.1: три равноправные кнопки
 * рядом читались как три разные фичи, хотя это одно намерение.
 */
export type FillMethod = 'rules' | 'rotation' | 'targets' | 'last_season';

export interface FillMethodOption {
  method: FillMethod;
  title: string;
  hint: string;
  /** Почему пункт недоступен. `null` — доступен. Текст показываем человеку. */
  disabledReason: string | null;
}

/**
 * Меню способов заполнения. «Как в прошлом сезоне» пока погашен ЧЕСТНО, а не
 * спрятан: на бэке способ `last_season` контрактом объявлен, но ручкой не
 * принимается (SRS §1). Спрятанный пункт вернулся бы вопросом «а где повтор
 * прошлого года», погашенный с подписью — отвечает на него сам.
 */
export function fillMethodOptions(variantStatus: string): FillMethodOption[] {
  const notDraft =
    variantStatus === 'draft'
      ? null
      : 'Вариант утверждён как рабочий бюджет — автосбор его не переписывает. Сделайте копию варианта.';
  return [
    {
      method: 'rules',
      title: 'По правилам',
      hint: 'по предшественнику, поле за полем',
      disabledReason: null,
    },
    {
      method: 'rotation',
      title: 'По севообороту',
      hint: 'подбор по кольцу севооборота',
      disabledReason: null,
    },
    {
      method: 'targets',
      title: 'По целям на культуры',
      hint: 'от целевой структуры площадей — собрать план автоматически',
      disabledReason: notDraft,
    },
    {
      method: 'last_season',
      title: 'Как в прошлом сезоне',
      hint: 'повторить раскладку прошлого года',
      disabledReason: 'Скоро: повтор прошлого сезона пока готовится.',
    },
  ];
}

// ── Происхождение строки плана ───────────────────────────────────────────────

/**
 * Пометка происхождения строки. Неизвестное происхождение (`null`) читается
 * как ручное — так же, как его трактует пересбор: то, чей автор неизвестен,
 * автоматика не переписывает.
 */
export function originBadge(
  origin: PlanRowOrigin | null | undefined,
): { label: string; variant: 'primary' | 'secondary' | 'accent-brand' } | null {
  switch (origin) {
    case 'auto':
      return { label: 'собрано автоматически', variant: 'accent-brand' };
    case 'rules':
      return { label: 'назначено правилом', variant: 'primary' };
    case 'import':
      return { label: 'загружено из файла', variant: 'secondary' };
    case 'manual':
      return { label: 'вручную', variant: 'secondary' };
    default:
      return null;
  }
}

// ── Цель: форма и арифметика ─────────────────────────────────────────────────

/** Строка целевой структуры, как её ведёт человек в форме. */
export interface TargetRow {
  /** Локальный ключ строки: культуру можно ещё не выбрать, а строка уже есть. */
  key: string;
  cropId: string;
  /** Что ввёл человек в текущей единице (га или % пашни) — как строка. */
  input: string;
}

export type TargetUnit = 'ha' | 'pct';

/** Число из поля ввода: запятая — тоже десятичный разделитель, агроном пишет ею. */
export function parseNumber(input: string): number {
  const normalized = input.replace(/\s/g, '').replace(',', '.');
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

/**
 * Строка формы → гектары. Единица переключается НА ВСЮ таблицу (FR-3): если бы
 * её выбирали построчно, агроном спорил бы с округлением в каждой строке.
 */
export function rowHa(row: TargetRow, unit: TargetUnit, arableHa: number): number {
  const value = parseNumber(row.input);
  return unit === 'ha' ? value : (value * arableHa) / 100;
}

export function targetsTotalHa(
  rows: readonly TargetRow[],
  unit: TargetUnit,
  arableHa: number,
): number {
  return rows.reduce((sum, r) => sum + (r.cropId ? rowHa(r, unit, arableHa) : 0), 0);
}

export interface TargetsBalance {
  totalHa: number;
  pct: number;
  /** Сколько цели НЕ хватает до пашни. Ноль и меньше — недобора нет. */
  freeHa: number;
  /** На сколько цель больше пашни. Больше нуля — считать нельзя (FR-5). */
  overshootHa: number;
  /** Можно ли считать раскладку: есть хотя бы одна культура и нет перебора. */
  canPreview: boolean;
}

/**
 * Сверка цели с пашней. Перебор — стоп (раскладка была бы заведомо
 * невыполнимой, и агроном узнал бы об этом только по хвосту «не разложилось»).
 * Недобор — разрешён: свободное остаётся пустым, «дотягивать» культуру сверх
 * цели мы не будем (решение founder 04.09 п.5).
 */
export function targetsBalance(
  rows: readonly TargetRow[],
  unit: TargetUnit,
  arableHa: number,
): TargetsBalance {
  const totalHa = targetsTotalHa(rows, unit, arableHa);
  const filled = rows.filter((r) => r.cropId && rowHa(r, unit, arableHa) > 0);
  // Полгектара допуска — ровно тот же порог, что у сервера: иначе кнопка
  // гаснет там, где ручка посчитала бы, и наоборот.
  const overshootHa = totalHa - arableHa > 0.5 ? totalHa - arableHa : 0;
  return {
    totalHa,
    pct: arableHa > 0 ? (totalHa / arableHa) * 100 : 0,
    freeHa: Math.max(arableHa - totalHa, 0),
    overshootHa,
    canPreview: filled.length > 0 && overshootHa === 0,
  };
}

/** Тело запроса из формы: сервер считает только в гектарах. */
export function toTargetsPayload(
  rows: readonly TargetRow[],
  unit: TargetUnit,
  arableHa: number,
): { crop_id: string; target_ha: number; position: number }[] {
  return rows
    .filter((r) => r.cropId && rowHa(r, unit, arableHa) > 0)
    .map((r, index) => ({
      crop_id: r.cropId,
      // Порядок строк = приоритет разбора (FR-11), поэтому позиция берётся из
      // порядка в таблице, а не из сортировки по площади.
      position: index + 1,
      target_ha: Math.round(rowHa(r, unit, arableHa) * 10) / 10,
    }));
}

// ── Ответы ручек ─────────────────────────────────────────────────────────────

export interface AutoBuildWhy {
  predecessor_crop_id: string | null;
  predecessor_crop_name: string | null;
  predecessor_verdict: string;
  return_norm_years: number | null;
  years_since: number | null;
  target_left_ha_before: number;
  displaced_crop_ids: string[];
}

export interface AutoBuildAssigned {
  field_id: string;
  field_name: string;
  area_ha: number;
  crop_id: string;
  crop_name: string | null;
  yield_t_ha: number | null;
  why: AutoBuildWhy;
  /** Готовый текст «почему так?» — собран сервером, экран его не сочиняет. */
  explanation: string;
}

export interface AutoBuildUnresolved {
  field_id: string;
  field_name: string;
  area_ha: number;
  reason: string;
  message: string;
  detail: Record<string, unknown>;
}

export interface AutoBuildReasonGroup {
  reason: string;
  fields: number;
  area_ha: number;
}

export interface AutoBuildByCrop {
  crop_id: string;
  crop_name: string | null;
  target_ha: number;
  assigned_ha: number;
  deviation_ha: number;
  fields: number;
  avg_yield_c_ha: number | null;
  revenue_rub: number | null;
}

export interface AutoBuildTotals {
  assigned_ha: number;
  assigned_fields: number;
  target_deviation_ha: number;
  conflicts: number;
  manual_untouched_fields: number;
  manual_untouched_ha: number;
  /** `null` — у роли нет права на деньги: колонки нет, а не нули (SRS §2). */
  revenue_rub: number | null;
}

export interface AutoBuildPreview {
  scenario_id: string;
  season_year: number;
  digest: string;
  arable_ha: number;
  targets_ha: number;
  free_fields: number;
  totals: AutoBuildTotals;
  by_crop: AutoBuildByCrop[];
  assigned: AutoBuildAssigned[];
  unresolved: AutoBuildUnresolved[];
  reason_groups: AutoBuildReasonGroup[];
  revenue_note: string | null;
  /** В варианте нет ни одного правила севооборота (AK-809) — раскладка
   * гарантированно пустая, и причина не «поле за полем», а одна на весь
   * прогон. */
  has_rules: boolean;
}

export interface AutoBuildApplyResult {
  run_id: string;
  applied_fields: number;
  applied_ha: number;
  replaced_fields: number;
  unresolved_fields: number;
  manual_untouched_fields: number;
}

export interface AutoBuildRun {
  run_id: string;
  method: string;
  targets: { crop_id: string; target_ha: number; position: number; unit?: string }[];
  stats: Record<string, unknown>;
  created_at: string;
  created_by_user_id: string | null;
  created_by_name: string | null;
}

export interface TargetDefault {
  crop_id: string;
  crop_name: string | null;
  fact_ha: number;
}

export interface ClearAutoResult {
  archived_fields: number;
  archived_ha: number;
}

/** Группа причин отказа — заголовком человека, а не кодом словаря. */
export function reasonGroupLabel(reason: string): string {
  switch (reason) {
    case 'norm_violation':
      return 'норматив возврата культуры';
    case 'no_history':
      return 'нет истории культур';
    case 'target_exhausted':
      return 'цель по культуре уже набрана';
    case 'no_compatible_crop':
      return 'ни одна культура из цели не подходит';
    case 'fallow_suggested':
      return 'правила ведут поле к пару';
    case 'manual_occupied':
      return 'поле занято вручную';
    default:
      return 'другая причина';
  }
}

// ── Ручки ────────────────────────────────────────────────────────────────────

const base = (scenarioId: string) => `/api/v1/planning/scenarios/${scenarioId}`;

export const autoBuildKeys = {
  all: () => ['planning', 'auto-build'] as const,
  last: (scenarioId: string | null) => ['planning', 'auto-build', 'last', scenarioId ?? 'none'] as const,
  defaults: (scenarioId: string | null) =>
    ['planning', 'auto-build', 'target-defaults', scenarioId ?? 'none'] as const,
};

/**
 * Раскладка ЗАПИСЬ не меняет — но это всё равно мутация react-query, а не
 * запрос: цель едет телом на десятки строк, кешировать её по ключу бессмысленно
 * и вредно (два одинаковых прогона по разным данным дали бы разный ответ).
 */
export function useAutoBuildPreview() {
  return useMutation<
    AutoBuildPreview,
    Error,
    { scenarioId: string; targets: ReturnType<typeof toTargetsPayload>; unit: TargetUnit }
  >({
    mutationFn: ({ scenarioId, targets, unit }) =>
      api.post<AutoBuildPreview>(`${base(scenarioId)}/auto-build/preview`, {
        method: 'targets',
        unit,
        targets,
      }),
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/**
 * Инвалидация после записи трогает те же четыре неймспейса, что и остальные
 * массовые правки структуры: строки варианта, покрытие техкарт, срез СПП и
 * подбор по кольцу. Односторонняя инвалидация давала бы соседний экран с
 * прежней структурой — дефект, за который уже ловили #803/#820.
 */
function useInvalidateStructure() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
    void qc.invalidateQueries({ queryKey: techCardKeys.all() });
    void qc.invalidateQueries({ queryKey: cropStructureKeys.all() });
    void qc.invalidateQueries({ queryKey: rotationMatchKeys.all() });
    // Строки варианта живут под тем же префиксом `plan-variant`, что и сводка,
    // — их накрывает `planVariantKeys.all()`. Отдельно гасим снимок прогона:
    // после записи «последний прогон» это уже другой прогон.
    void qc.invalidateQueries({ queryKey: autoBuildKeys.all() });
  };
}

/** Ответ ручки на конфликт «данные изменились» — по нему экран пересчитывает. */
export function isStalePreview(err: unknown): boolean {
  return (err as { status?: number })?.status === 409;
}

export function useApplyAutoBuild() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    AutoBuildApplyResult,
    Error,
    {
      scenarioId: string;
      targets: ReturnType<typeof toTargetsPayload>;
      unit: TargetUnit;
      digest: string;
    }
  >({
    mutationFn: ({ scenarioId, targets, unit, digest }) =>
      api.post<AutoBuildApplyResult>(`${base(scenarioId)}/auto-build/apply`, {
        method: 'targets',
        unit,
        targets,
        digest,
      }),
    // В `onSettled`: даже отказ ручки не гарантирует, что на сервере ничего не
    // изменилось (таймаут после записи), и экран обязан перечитать факт.
    onSettled: () => invalidate(),
    onSuccess: (res) =>
      toast.success(
        `Записано: ${res.applied_fields} полей собрано автоматически. ` +
          `Ручные строки не тронуты (${res.manual_untouched_fields}).`,
      ),
    onError: (err) => {
      if (isStalePreview(err)) return; // текст показывает сам экран, рядом с кнопкой
      toast.error(techCardErrorText(err));
    },
  });
}

export function useClearAutoRows() {
  const invalidate = useInvalidateStructure();
  return useMutation<ClearAutoResult, Error, { scenarioId: string }>({
    mutationFn: ({ scenarioId }) => api.delete<ClearAutoResult>(`${base(scenarioId)}/rows/auto`),
    onSettled: () => invalidate(),
    onSuccess: (res) =>
      toast.success(
        res.archived_fields === 0
          ? 'Автоматических строк в варианте нет — убирать нечего.'
          : `Убрано автоматических строк: ${res.archived_fields}. Ручные строки не тронуты.`,
      ),
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/** Последний прогон варианта — им предзаполняется форма цели (FR-4). */
export function useLastAutoBuild(scenarioId: string | null, enabled = true) {
  return useQuery<AutoBuildRun | null, Error>({
    queryKey: autoBuildKeys.last(scenarioId),
    queryFn: () => api.get<AutoBuildRun | null>(`${base(scenarioId as string)}/auto-build/last`),
    enabled: !!scenarioId && enabled,
    staleTime: 15_000,
  });
}

/** Структура ФАКТА прошлого сезона — «Взять из прошлого сезона». */
export function useTargetDefaults(scenarioId: string | null, enabled = true) {
  return useQuery<TargetDefault[], Error>({
    queryKey: autoBuildKeys.defaults(scenarioId),
    queryFn: () =>
      api.get<TargetDefault[]>(`${base(scenarioId as string)}/auto-build/target-defaults`),
    enabled: !!scenarioId && enabled,
    staleTime: 60_000,
  });
}
